import { useRescheduleCruiseSale } from 'api-hooks/sale/cruise';
import { SaleDetailModel } from 'api-hooks/sale/model';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useNavigation from 'hooks/use-navigation';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import {
  FormContent,
  FormDivider,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { CruiseRescheduleFormType, CruiseRescheduleSchema } from './form-type';
import useGetDefaultValues from './hooks/use-get-default-values';
import CruiseItems from './items';
import SummaryCruiseReschedule from './summary';
import { totalCruiseRescheduleCalculation } from './total-calculation';
import RescheduleHistoryTable from '../reschedule-history-table';

interface Props {
  cruiseSale: SaleDetailModel;
}

export default function CruiseRescheduleForm(props: Props) {
  const { t } = useTranslation();
  const { cruiseSale } = props;
  const { navigate, close } = useNavigation();

  const { mutateAsync } = useRescheduleCruiseSale();

  const defaultValues = useGetDefaultValues({ cruiseSale });

  const methods = useForm<CruiseRescheduleFormType>({
    defaultValues,
    resolver: useYupValidationResolver(CruiseRescheduleSchema),
    mode: 'onChange',
  });

  const onCancel = () => {
    close();
    navigate(NavigationRoutes.CruiseSalesView, {
      id: cruiseSale.id,
    });
  };

  const onSubmit = async (values: CruiseRescheduleFormType) => {
    try {
      values.total = totalCruiseRescheduleCalculation(values.cruiseSales);
      const cruiseSales = values.cruiseSales
        .filter((cs) => cs.cruiseSaleDetails.some((csd) => csd.isSelected))
        .map((cs) => ({
          ...cs,
          startedAt: values.cruiseSales[0].startedAt,
          endedAt: values.cruiseSales[0].endedAt,
          cruiseSaleGuests: cs.cruiseSaleGuests
            .filter((csg) => csg.isSelected)
            .map((csg) => ({ ...csg, isSelected: undefined })),
          cruiseSaleDetails: cs.cruiseSaleDetails
            .filter((csd) => csd.isSelected)
            .map((csd) => ({ ...csd, isSelected: undefined })),
          cruiseSaleAddOns: cs.cruiseSaleAddOns
            .filter((csg) => csg.isSelected)
            .map((csao) => ({ ...csao, isSelected: undefined })),
        }));

      if (!cruiseSales.length) {
        notification.error({ message: t('error:at_least_select_one_item') });
        return;
      }

      await mutateAsync({
        cruiseSaleId: cruiseSale.id,
        body: { ...values, cruiseSales },
      });
      onCancel();
    } catch (e) {
      notification.error({ message: e.message });
    }
  };

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      noReset
    >
      <FormContent>
        <FormHeader
          title={t('reschedule:reschedule_cruise')}
          data={cruiseSale}
          noDelete
          onCancel={onCancel}
        />
        <div>
          <SectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="invoiceNumber"
                label={t('common:invoice_number')}
                disabled
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text"
                name="customer"
                label={t('common:customer')}
                disabled
              />
            </HalfContainer>
          </SectionContainer>
          <HalfContainer>
            <Input
              type="date-time"
              name="transactionAt"
              label={t('common:transaction_at')}
              clearable={false}
              required
            />
          </HalfContainer>
          <Separator gap={32} direction="vertical">
            <RescheduleHistoryTable type="cruise" saleId={cruiseSale.id} />
          </Separator>

          <Separator gap={16} direction="vertical">
            <CruiseItems />
          </Separator>
          <FormDivider />
        </div>
      </FormContent>
      <SummaryCruiseReschedule />
    </Form>
  );
}
